import clsx from "clsx";
import { AutomationContext } from "context/AutomationContext";
import { useContext, useEffect, useState, useRef } from "react";
import { SvgBack } from "ui/svgs";
import { Tabs } from "antd";
import Regions from "ui/automation/regions";
import CrossLines from "ui/automation/cross-lines";
import Schedule from "ui/automation/schedule";
import Action from "ui/automation/action";
import { AuthContext } from "context/AuthContext";
import PubSub from "pubsub-js";
import Modal from "react-modal";
import _ from "lodash";
import { ConfirmModalStyles } from "lib/constants";
import { showToast } from "ui/components/common";
import { checkRules, checkLineRules } from "lib/utils";
import Notes from "ui/automation/notes";
import { WsResDataType, AdvancedAISettingType } from "lib/types";
import { useAutomationService } from "./service";
import AdvancedAISetting from "ui/automation/advancedAI";
import "./style.scss";
import type { TabsProps } from "antd";

export default function Page() {
  const { sendUpdateCameraRuleRequest, updateAdvancedAISetting } =
    useAutomationService();

  const [saving, setSaving] = useState(false);
  const [tabIndex, setTabIndex] = useState("Intrusion");
  const [modalOpen, setModalOpen] = useState(false);

  const [continueBtnDisabled, setContinueBtnDisabled] = useState(false);

  const {
    streamingCamera,
    cameraAlertDetections,
    setCameraAlertDetections,
    crossLineDetections,
    setCrossLineDetections,
    supportedEventTypes,
    actions,
    setActions,
    alertSchedules,
    setAlertSchedules,
    advancedAISetting,
    setAdvancedAISetting,
    showMask,
    setResetedData,
    reseteAvailable,
    reset,
    note,
    setNote,
  } = useContext(AutomationContext);

  const { user } = useContext(AuthContext);

  const cameraIdRef = useRef(null);

  useEffect(() => {
    cameraIdRef.current = streamingCamera?.cameraId;
  }, [streamingCamera]);

  const saveAlertRules = async () => {
    if (!checkRules(cameraAlertDetections)) {
      showToast(1, "Please draw the region at least 3 points for all regions!");
      return;
    }
    if (!checkLineRules(crossLineDetections)) {
      showToast(1, "Please draw the region at least 2 points for all lines!");
      return;
    }
    setSaving(true);
    const res = await updateAdvancedAISetting(
      {
        setting: advancedAISetting,
      },
      +streamingCamera.cameraId,
      +streamingCamera?.orgId,
    );
    if (res.status !== 200) {
      setSaving(false);
      showToast(1, "Failed to save advanced AI settings");
      return;
    }

    const data = [];
    data.push({
      cameraId: +streamingCamera.cameraId,
      remoteIndex: +streamingCamera.remoteIndex,
      algorithms: [
        {
          algo: "intrusion",
          detections: cameraAlertDetections,
        },
        {
          algo: "cross_line",
          detections: crossLineDetections,
        },
      ],
      schedules: alertSchedules,
      actions: actions,
      note: note,
    });

    try {
      const res = await sendUpdateCameraRuleRequest(
        data,
        +streamingCamera.orgId,
      );

      const result = (res as WsResDataType)?.alertUpdateCameraRuleReply?.result;
      let hasError = true;

      if (result) {
        const curRes = result?.find(
          (item) => item.cameraId === +streamingCamera.cameraId,
        );
        if (!curRes) {
          return;
        }
        if (!curRes.error) {
          hasError = false;
        }
      }
      if (hasError) {
        showToast(
          1,
          "There was a problem saving your changes. Please try again.",
        );
        return;
      }
      const copy = _.cloneDeep({
        cameraId: +streamingCamera.cameraId,
        algorithms: [
          {
            algo: "intrusion",
            detections: cameraAlertDetections,
          },
          {
            algo: "cross_line",
            detections: crossLineDetections,
          },
        ],
        schedules: alertSchedules,
        note: note,
        advancedAISetting,
        actions,
      });
      PubSub.publish("oneCameraRuleSaved", copy);
      showToast(0, "The rules for the camera has been saved successfully!");
      // fix cameraid changed before saving successfully
      if (cameraIdRef.current === streamingCamera.cameraId) {
        setResetedData({
          alertSchedules: copy.schedules,
          advancedAISetting,
          algorithms: copy.algorithms,
          actions: copy.actions,
          note: copy.note,
        });
      }
    } catch (error) {
      showToast(
        1,
        "There was a problem saving your changes. Please try again. Error:" +
          error,
      );
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    setTabIndex("Intrusion");
    setSaving(false);
  }, [streamingCamera]);

  function onNoteChange(value) {
    setNote(value);
  }

  const items: TabsProps["items"] = [
    {
      key: "Intrusion",
      label: "Intrusion",
      children: (
        <div className="intrusion-container">
          <Regions
            streamingCamera={streamingCamera}
            setCameraAlertDetections={(detections) =>
              setCameraAlertDetections(detections)
            }
            cameraAlertDetections={cameraAlertDetections}
            supportedEventTypes={supportedEventTypes}
          />
        </div>
      ),
    },

    {
      key: "Schedule",
      label: "Schedule",
      children: (
        <div className="adaptive-padding">
          <Schedule
            alertSchedules={alertSchedules}
            setAlertSchedules={setAlertSchedules}
            setContinueBtnDisabled={setContinueBtnDisabled}
          />
        </div>
      ),
    },
    {
      key: "Action",
      label: "Action",
      children: (
        <div className="adaptive-padding">
          <Action setActions={setActions} actions={actions} />{" "}
        </div>
      ),
    },
    {
      key: "Advanced AI",
      label: "Advanced AI",
      children: (
        <div className="advanced-ai-container">
          <AdvancedAISetting
            advancedAISetting={advancedAISetting}
            setAdvancedAISetting={(setting) => {
              setAdvancedAISetting(setting);
            }}
            camera={streamingCamera}
          />
        </div>
      ),
    },
    {
      key: "Notes",
      label: "Notes",
      children: (
        <div className="notes-container">
          <Notes
            streamingCamera={streamingCamera}
            note={note}
            onChange={onNoteChange}
          />
        </div>
      ),
    },
  ];

  if (streamingCamera?.capabilities?.supportCrossLine) {
    items.splice(1, 0, {
      key: "Cross Line",
      label: "Cross Line",
      children: (
        <div className="cross-line-container">
          <CrossLines
            streamingCamera={streamingCamera}
            setCrossLines={(detections) => setCrossLineDetections(detections)}
            crossLines={crossLineDetections}
            supportedEventTypes={supportedEventTypes}
          />
        </div>
      ),
    });
  }

  return (
    <div
      className="automationPage relative h-full w-full "
      style={showMask ? { background: "rgba(0,0,0,0.4)" } : {}}
    >
      {streamingCamera ? (
        <div className="flex flex-col w-full h-full   bg-white  overflow-y-hidden max-h-full">
          {showMask && (
            <div
              className="absolute top-0 left-0 h-full w-full z-20"
              style={{ background: "rgba(0,0,0,0.4)" }}
            ></div>
          )}
          {/* <div className="h-12 w-full flex items-center gap-10 mb-8 text-xl font-semibold">
            <button
              className="w-12 h-12  flex items-center justify-center"
              onClick={() => {
                setModalOpen(true);
              }}
              disabled={!reseteAvailable}
            >
              <SvgBack
                width="16"
                className={reseteAvailable ? "fill-zinc-500" : "fill-zinc-300"}
              />
            </button>
            Edit Alert Rules
          </div> */}
          <Tabs
            destroyInactiveTabPane={true}
            activeKey={tabIndex}
            items={items}
            onChange={setTabIndex}
            className="w-full"
            indicator={null}
          />

          <div className="pt-6 w-full">
            <button
              className={clsx(
                "float-right  flex justify-center h-[56px] w-[82px] items-center rounded-2xl bg-blue-600 px-6 text-base font-medium text-white transition-colors hover:bg-blue-500 disabled:bg-blue-200 disabled:hover:bg-blue-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600",
              )}
              disabled={saving || continueBtnDisabled || !reseteAvailable}
              onClick={saveAlertRules}
            >
              {saving ? (
                <div
                  className="ml-1 text-blue-500  inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                  role="status"
                ></div>
              ) : (
                "Save"
              )}
            </button>
          </div>
          <Modal
            isOpen={modalOpen}
            style={ConfirmModalStyles}
            contentLabel="Example Modal"
          >
            <h1 className="text-xl font-bold">You have unsaved changes</h1>
            <div className="text-base mt-4">
              Are you sure you want to discard your unsaved changes?
            </div>

            <div className="float-right mt-6">
              <button
                className="rounded-2xl bg-white px-6 py-4 border border-blue-500 hover:border-blue-400 text-sm  transition-colors text-blue-500 hover:text-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
                onClick={() => {
                  setModalOpen(false);
                }}
              >
                Cancel
              </button>
              <button
                className="ml-4 rounded-2xl px-6 py-4 bg-blue-500 text-sm font-medium text-white transition-colors hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
                onClick={() => {
                  setModalOpen(false);
                  reset();
                }}
              >
                Discard
              </button>
            </div>
          </Modal>
        </div>
      ) : null}
    </div>
  );
}
